import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

interface DashboardCardShellProps {
  title: string;
  icon?: LucideIcon;
  /** This card's own query loading state (per-card skeleton). */
  isLoading: boolean;
  /** This card's own query error (per-card error + retry). */
  error: Error | null;
  /** Refetch for THIS card's query. */
  onRetry?: () => void;
  children: ReactNode;
}

/**
 * M3 (#573): per-card shell that reproduces the house per-card skeleton +
 * "Couldn't load" + Retry idiom (`AnalyticsChartCard`) inside a titled shadcn
 * Card. Each dashboard card owns its OWN query state, so its skeleton/error/retry
 * are driven off that single query — never a collapsed page-level state.
 */
export function DashboardCardShell({
  title,
  icon: Icon,
  isLoading,
  error,
  onRetry,
  children,
}: DashboardCardShellProps): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {Icon && <Icon className="h-5 w-5" />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : error ? (
          <div className="flex h-40 flex-col items-center justify-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <p className="text-sm text-muted-foreground">Couldn&rsquo;t load</p>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                Retry
              </Button>
            )}
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}
